import { useAuth } from '@/contexts/auth-context';
import { useBillingContextQuery } from '@/hooks/queries/use-billing-context-query';
import {
  closedPaywallState,
  consumedBillingReturnState,
  paywallParsers,
  paywallScopeKey,
  serializePaywallUrl,
  upgradePaywallFor,
  type PaywallPlan,
  type PaywallType,
  type UpgradeFeature,
} from '@/libs/paywall-url-state';
import { useUpgradeModalStore } from '@/stores/upgrade-modal-store';
import { PlanTier, type PlanLimit, type PlanLimitReachedData } from '@fluenceur/types';
import { useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/** URL-backed presentation state. Ephemeral API refusals never grant access. */
export function usePaywall() {
  const [url, setPaywall] = useQueryStates(paywallParsers, {
    history: 'push',
    shallow: false,
  });
  const { paywall, plan, minimumPlan, operationId, session_id, shopify_free_approval } = url;
  const isBillingReturn =
    operationId !== null || session_id !== null || shopify_free_approval !== null;
  const { user } = useAuth();
  const { data: billingContext } = useBillingContextQuery();
  const planTier = billingContext?.effectiveTier ?? PlanTier.FREE;
  const location = useLocation();
  const userId = user?.id;
  const organizationId = user?.currentBrand?.organizationId ?? undefined;
  const brandId = user?.currentBrandId ?? undefined;
  const scope = useMemo(
    () => ({ userId, organizationId, brandId }),
    [userId, organizationId, brandId]
  );
  const scopeKey = paywallScopeKey(scope);
  const origin = serializePaywallUrl(
    location.pathname + location.search + location.hash,
    closedPaywallState
  );

  const open = useCallback(
    (
      type: PaywallType,
      preferredPlan: PaywallPlan | null,
      payload: PlanLimitReachedData | null = null,
      onClose?: () => void,
      minimumPlan: 'essential' | 'growth' | null = null
    ) => {
      useUpgradeModalStore.getState().setPaywallContext({
        paywall: type,
        scope,
        origin,
        planLimit: payload,
        ...(onClose ? { onClose } : {}),
      });
      void setPaywall({ paywall: type, plan: preferredPlan, minimumPlan });
    },
    [scope, origin, setPaywall]
  );

  const openPlans = useCallback(
    (preferredPlan: PaywallPlan = 'free', options?: { onClose?: () => void }) =>
      open('plans', preferredPlan, null, options?.onClose),
    [open]
  );
  const openUpgradeModal = useCallback(
    (feature: UpgradeFeature = 'generic', preferredTier?: PlanTier) => {
      const target = upgradePaywallFor(feature, preferredTier, planTier);
      open(
        target.paywall,
        null,
        null,
        undefined,
        target.paywall === 'plans' ? (target.plan === 'growth' ? 'growth' : 'essential') : null
      );
    },
    [open, planTier]
  );
  const openPlanLimitModal = useCallback(
    (payload: PlanLimitReachedData, options?: { onClose?: () => void }) =>
      open(payload.limit, null, payload, options?.onClose),
    [open]
  );
  const openInferredPlanLimitModal = useCallback((limit: PlanLimit) => open(limit, null), [open]);
  const closePaywall = useCallback(
    (options?: { notify?: boolean; history?: 'push' | 'replace' }) => {
      const context = useUpgradeModalStore.getState().context;
      const onClose =
        options?.notify !== false &&
        context?.paywall === paywall &&
        context.origin === origin &&
        paywallScopeKey(context.scope) === scopeKey
          ? context.onClose
          : undefined;
      useUpgradeModalStore.getState().closeUpgradeModal();
      const closed =
        paywall === 'plans' && isBillingReturn
          ? { ...closedPaywallState, ...consumedBillingReturnState }
          : closedPaywallState;
      return setPaywall(closed, { history: options?.history ?? 'push' }).then(() => onClose?.());
    },
    [paywall, isBillingReturn, origin, scopeKey, setPaywall]
  );

  return {
    paywall,
    plan,
    minimumPlan,
    scopeKey,
    scope,
    origin,
    openPlans,
    openUpgradeModal,
    openPlanLimitModal,
    openInferredPlanLimitModal,
    closePaywall,
  };
}
